import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  use,
  useMemo,
  useRef,
  useSyncExternalStore,
  type ReactNode,
} from "react";

import {
  completeSignIn,
  ensureSession,
  getSession,
  signIn,
  signOut,
  subscribeSession,
  type CallbackParams,
  type Me,
  type SessionState,
} from "../lib/auth.ts";
import {
  canSeeCosts,
  hasPermission,
  type Permission,
} from "../lib/permissions.ts";

import { SHELL_TEXTS } from "./texts.ts";

/**
 * La sesión, para React y para el router.
 *
 * La lógica vive en `lib/auth.ts`, fuera de React, porque también la usa el
 * cliente HTTP. Aquí se la envuelve en un `SessionStore`: lo mismo que ofrece
 * `lib/auth.ts`, pero como un objeto que se pasa. El router lo recibe en su
 * contexto y el `SessionProvider` como prop, y así una prueba puede darles una
 * sesión hecha --con los permisos que quiera probar-- sin SSO ni red.
 */
export interface SessionStore {
  readonly getState: () => SessionState;
  readonly subscribe: (listener: () => void) => () => void;
  /** Recupera la sesión o, si no hay, va al SSO. Lo llama la ruta al entrar. */
  readonly ensure: (returnTo: string) => Promise<SessionState>;
  /** La vuelta del SSO. Devuelve a dónde volver, o `null` si falló. */
  readonly complete: (params: CallbackParams) => Promise<string | null>;
  readonly signIn: (returnTo: string) => Promise<void>;
  readonly signOut: (beforeLeave: () => void) => Promise<void>;
}

/** La de verdad. */
export const liveSession: SessionStore = {
  getState: getSession,
  subscribe: subscribeSession,
  ensure: ensureSession,
  complete: completeSignIn,
  signIn: (returnTo) => signIn({ returnTo }),
  signOut,
};

export interface Session {
  readonly state: SessionState;
  /**
   * Quién está dentro, o `null` si nadie.
   *
   * Mientras se vuelve al SSO porque la sesión caducó, sigue siendo quien
   * era: la aplicación sigue a la vista hasta que el navegador se vaya, y no
   * tiene por qué esconder botones en ese momento.
   */
  readonly me: Me | null;
  /**
   * Si la persona tiene ese permiso. SOLO para decidir qué se enseña: el
   * backend valida cada llamada igual, tenga o no el botón a la vista.
   */
  readonly can: (permission: Permission) => boolean;
  /**
   * Si ve los costos --dólares y tokens de IA--: solo el superadmin. Es la
   * excepción a `can` (`lib/permissions.ts`, `canSeeCosts`), y como `can`,
   * solo decide qué se enseña.
   */
  readonly canSeeCosts: boolean;
  /** Ir al SSO a mano: el botón de las pantallas de error. */
  readonly signIn: () => void;
  /**
   * Volver a intentar entrar cuando no se llegó al servidor: lo mismo que al
   * arrancar, y si resulta que no hay sesión, al SSO.
   */
  readonly retry: () => void;
  /**
   * Cerrar sesión. `beforeLeave` es lo que quien llama tiene que soltar
   * antes --el turno del chat--; la caché la suelta esto.
   */
  readonly signOut: (beforeLeave?: () => void) => Promise<void>;
  readonly store: SessionStore;
}

const SessionContext = createContext<Session | null>(null);

const here = (): string =>
  `${window.location.pathname}${window.location.search}`;

export function SessionProvider({
  store,
  children,
}: {
  readonly store: SessionStore;
  readonly children: ReactNode;
}) {
  const state = useSyncExternalStore(store.subscribe, store.getState);
  const queryClient = useQueryClient();
  const lastMe = useRef<Me | null>(null);

  if (state.status === "authenticated") lastMe.current = state.me;
  if (state.status === "anonymous") lastMe.current = null;
  const me =
    state.status === "authenticated"
      ? state.me
      : state.status === "expired"
        ? lastMe.current
        : null;

  const session = useMemo(
    (): Session => ({
      state,
      me,
      can: (permission) =>
        me !== null && hasPermission(me.permissions, permission),
      canSeeCosts: me !== null && canSeeCosts(me),
      signIn: () => {
        void store.signIn(here());
      },
      retry: () => {
        void store.ensure(here());
      },
      signOut: (beforeLeave) =>
        store.signOut(() => {
          beforeLeave?.();
          // Lo que hay en caché es de quien se va: no puede verlo quien entre.
          queryClient.clear();
        }),
      store,
    }),
    [state, me, store, queryClient],
  );

  return <SessionContext value={session}>{children}</SessionContext>;
}

export const useSession = (): Session => {
  const session = use(SessionContext);
  if (session === null) {
    throw new Error(SHELL_TEXTS.sessionOutside);
  }
  return session;
};
